import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import { useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

export type TeamsContext = {
  teamsClient: typeof microsoftTeams | undefined;
  teamsContext: microsoftTeams.app.Context | undefined;
  authCode: string | undefined;
  renewAuthCode: (() => Promise<void>) | undefined;
  shouldRequestConsent: (() => void) | undefined;
  /** Resolves once the scopes are granted (silently or via the popup). */
  shouldRequestScopedConsent: ((scopes: string[]) => Promise<void>) | undefined;
  consentInProgress: boolean;
  consent: boolean;
};

export const TeamsContext = React.createContext<TeamsContext>({
  teamsClient: undefined,
  teamsContext: undefined,
  authCode: undefined,
  renewAuthCode: undefined,
  shouldRequestConsent: undefined,
  shouldRequestScopedConsent: undefined,
  consentInProgress: false,
  consent: true,
});

// A consent granted this recently that the gateway still refuses is not a
// consent problem: asking again would loop on a popup that closes at once.
const CONSENT_COOLDOWN_MS = 30 * 1000;

type PendingConsent = {
  promise: Promise<void>;
  resolve: () => void;
  reject: (reason: unknown) => void;
};

const createPendingConsent = (): PendingConsent => {
  let resolve!: () => void;
  let reject!: (reason: unknown) => void;
  const promise = new Promise<void>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

export const TeamsContextProvider = (props: { children: ReactNode }) => {
  const { children } = props;

  const queryClient = useQueryClient();
  const { instance, accounts } = useMsal();

  const [teamsClient] = useState<typeof microsoftTeams>(microsoftTeams);
  const [teamsContext, setTeamsContext] = useState<
    microsoftTeams.app.Context | undefined
  >();
  const [authCode, setAuthCode] = useState<string | undefined>();
  const [consentInProgress, setConsentInProgress] = useState<boolean>(false);
  const [consent, setConsent] = useState<boolean>(true);
  const [pendingScopedConsent, setPendingScopedConsent] = useState<string[] | null>(null);
  const pendingConsentRef = useRef<PendingConsent | null>(null);
  const lastConsentAtRef = useRef(0);
  // Mirror of consentInProgress readable synchronously from callbacks.
  const consentInProgressRef = useRef(false);

  const updateConsentInProgress = useCallback((value: boolean) => {
    consentInProgressRef.current = value;
    setConsentInProgress(value);
  }, []);

  // Settles the shared scoped consent request, if any, and clears it.
  const settlePendingConsent = useCallback((error?: unknown) => {
    const pending = pendingConsentRef.current;
    pendingConsentRef.current = null;
    if (!pending) return;
    if (error === undefined) {
      pending.resolve();
    } else {
      pending.reject(error);
    }
  }, []);

  const retrieveContext = useCallback(() => {
    microsoftTeams.app.getContext().then((context) => {
      setTeamsContext(context);
    });
  }, []);

  const retrieveAuthCode = useCallback(async () => {
    console.log('Retrieving a new authentication token');
    try {
      const authCode = await microsoftTeams.authentication.getAuthToken();
      setAuthCode(authCode);
    } catch (error) {
      console.error('Failed to get auth: ', error);
      console.log(error);
    }
  }, []);

  const shouldRequestConsent = useCallback(() => {
    updateConsentInProgress(true);
    setConsent(false);
  }, [updateConsentInProgress]);

  /**
   * Every caller gets the same promise while a request is pending (the entry
   * is stored synchronously, before any await), so a single popup opens.
   */
  const shouldRequestScopedConsent = useCallback(
    (scopes: string[]): Promise<void> => {
      if (pendingConsentRef.current) {
        return pendingConsentRef.current.promise;
      }

      if (consentInProgressRef.current) {
        // The unscoped consent flow owns the popup and would never settle us.
        return Promise.reject(
          new Error('Another consent flow is already in progress')
        );
      }

      if (Date.now() - lastConsentAtRef.current < CONSENT_COOLDOWN_MS) {
        return Promise.reject(
          new Error('Consent was just granted, not asking again')
        );
      }

      const pending = createPendingConsent();
      pendingConsentRef.current = pending;

      const run = async () => {
        // Try to acquire token silently first (works if admin has already consented)
        if (accounts.length > 0) {
          try {
            await instance.acquireTokenSilent({
              scopes,
              account: accounts[0],
            });
            // Success! The admin has consented, no popup needed
            console.log('Token acquired silently for scopes:', scopes);
            lastConsentAtRef.current = Date.now();
            settlePendingConsent();
            queryClient.invalidateQueries().catch();
            return;
          } catch (error) {
            if (!(error instanceof InteractionRequiredAuthError)) {
              console.error('Silent token acquisition failed:', error);
            }
            // Continue to interactive consent
            console.log(
              'Silent acquisition failed, falling back to interactive consent'
            );
          }
        }

        // Fallback: open consent popup, settled by the scoped consent effect below
        setPendingScopedConsent(scopes);
        updateConsentInProgress(true);
      };

      run().catch((error) => settlePendingConsent(error));
      return pending.promise;
    },
    [instance, accounts, queryClient, settlePendingConsent, updateConsentInProgress]
  );

  useEffect(() => {
    // A scoped request owns the popup when one is pending.
    if (!consent && consentInProgress && !pendingScopedConsent) {
      console.log("Initiating user consent popup flow")
      microsoftTeams.authentication
        .authenticate({
          url: window.location.origin + '/auth-start',
          width: 600,
          height: 535,
        })
        .then(() => {
          console.log("Received user consent")
          updateConsentInProgress(false);
          setConsent(true);
          queryClient.invalidateQueries().catch();
        })
        .catch((error) => {
          console.error('Failed to get user consent: ', error);
          updateConsentInProgress(false);
          setConsent(false);
        });
    }
  }, [consent, consentInProgress, pendingScopedConsent, queryClient, updateConsentInProgress]);

  // Handle scoped consent (e.g., OneDrive permissions)
  useEffect(() => {
    if (pendingScopedConsent && consentInProgress) {
      const scopesParam = encodeURIComponent(pendingScopedConsent.join(','));
      console.log(`Initiating scoped consent popup flow for: ${pendingScopedConsent.join(', ')}`);
      microsoftTeams.authentication
        .authenticate({
          url: `${window.location.origin}/auth-start?scopes=${scopesParam}`,
          width: 600,
          height: 535,
        })
        .then(() => {
          console.log("Received scoped consent");
          lastConsentAtRef.current = Date.now();
          updateConsentInProgress(false);
          setPendingScopedConsent(null);
          queryClient.invalidateQueries().catch();
          settlePendingConsent();
        })
        .catch((error) => {
          console.error('Failed to get scoped consent: ', error);
          updateConsentInProgress(false);
          setPendingScopedConsent(null);
          settlePendingConsent(error ?? new Error('Consent failed'));
        });
    }
  }, [
    pendingScopedConsent,
    consentInProgress,
    queryClient,
    settlePendingConsent,
    updateConsentInProgress,
  ]);

  useEffect(() => {
    retrieveContext();
    retrieveAuthCode().catch();
  }, [retrieveContext, retrieveAuthCode]);

  useEffect(() => {
    if (authCode) {
      const expiresIn = getExpiration(authCode) - Date.now();
      console.log(`Authentication token will expire in ${expiresIn}ms`);
      const timeout = setTimeout(() => {
        retrieveAuthCode().catch();
      }, Math.min(1000 * 60, expiresIn));
      return () => clearTimeout(timeout);
    }
  }, [authCode, retrieveAuthCode]);

  return (
    <TeamsContext.Provider
      value={{
        teamsClient,
        teamsContext,
        authCode,
        renewAuthCode: retrieveAuthCode,
        consentInProgress,
        consent,
        shouldRequestConsent,
        shouldRequestScopedConsent,
      }}
    >
      {children}
    </TeamsContext.Provider>
  );
};

const getExpiration = (authCode: string) => {
  const payloadBase64 = authCode.split('.')[1];
  const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
  const decodedJwt = JSON.parse(window.atob(base64));
  return (decodedJwt['exp'] as number) * 1000;
};

export const useTeamsContext = () => {
  return useContext(TeamsContext);
};
